import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Save,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

interface JournalLine {
  id: string;
  accountId: string;
  accountCode?: string;
  accountName?: string;
  description: string;
  debit: number;
  credit: number;
}

// REFERENCE_TYPES will be built dynamically inside the component using t()

export function JournalEntryFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('accounting');
  const { user } = useAuthStore();

  const REFERENCE_TYPES = [
    { value: 'MANUAL', label: t('referenceTypes.MANUAL') },
    { value: 'ADJUSTMENT', label: t('referenceTypes.ADJUSTMENT') },
    { value: 'INVOICE', label: t('referenceTypes.INVOICE') },
    { value: 'PAYMENT', label: t('referenceTypes.PAYMENT') },
    { value: 'EXPENSE', label: t('referenceTypes.EXPENSE') },
    { value: 'BOOKING', label: t('referenceTypes.BOOKING') },
    { value: 'CLOSING', label: t('referenceTypes.CLOSING') },
  ];
  const isEditing = !!id;

  const [date, setDate] = useState(new Date().toISOString().split('T')[0] ?? '');
  const [description, setDescription] = useState('');
  const [referenceType, setReferenceType] = useState<string>('MANUAL');
  const [currency, setCurrency] = useState('TRY');
  const [lines, setLines] = useState<JournalLine[]>([
    { id: '1', accountId: '', description: '', debit: 0, credit: 0 },
    { id: '2', accountId: '', description: '', debit: 0, credit: 0 },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing entry if editing
  const { data: existingEntry, isLoading: isLoadingEntry } = trpc.journalEntry.byId.useQuery(
    id!,
    { enabled: isEditing }
  );

  // All active accounts for selection
  const { data: allAccounts, error: accountsError } = trpc.account.listForSelection.useQuery({
    isActive: true,
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.journalEntry.create.useMutation({
    onSuccess: (data) => {
      utils.journalEntry.list.invalidate();
      utils.journalEntry.stats.invalidate();
      navigate(`/travel/accounting/journal-entries/${data.id}`);
    },
    onError: (err) => {
      setError(err.message);
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.journalEntry.update.useMutation({
    onSuccess: () => {
      utils.journalEntry.list.invalidate();
      utils.journalEntry.byId.invalidate(id);
      navigate(`/travel/accounting/journal-entries/${id}`);
    },
    onError: (err) => {
      setError(err.message);
      setIsSubmitting(false);
    },
  });

  // Load existing entry data
  useEffect(() => {
    if (existingEntry) {
      setDate(new Date(existingEntry.date).toISOString().split('T')[0] ?? '');
      setDescription(existingEntry.description);
      setReferenceType(existingEntry.referenceType || 'MANUAL');
      setCurrency(existingEntry.currency);
      setLines(
        existingEntry.lines.map((line: {
          accountId: string;
          account: { code: string; name: string };
          description?: string | null;
          debit: number;
          credit: number;
        }, i: number) => ({
          id: String(i + 1),
          accountId: line.accountId,
          accountCode: line.account.code,
          accountName: line.account.name,
          description: line.description || '',
          debit: line.debit,
          credit: line.credit,
        }))
      );
    }
  }, [existingEntry]);

  const addLine = () => {
    setLines([
      ...lines,
      { id: String(Date.now()), accountId: '', description: '', debit: 0, credit: 0 },
    ]);
  };

  const removeLine = (id: string) => {
    if (lines.length > 2) {
      setLines(lines.filter((line) => line.id !== id));
    }
  };

  const updateLine = (id: string, field: keyof JournalLine, value: string | number) => {
    setLines(
      lines.map((line) => {
        if (line.id === id) {
          const updated = { ...line, [field]: value };
          // Clear the other amount when one is entered
          if (field === 'debit' && Number(value) > 0) {
            updated.credit = 0;
          } else if (field === 'credit' && Number(value) > 0) {
            updated.debit = 0;
          }
          // Set account info when account is selected
          if (field === 'accountId') {
            const account = allAccounts?.accounts.find(a => a.id === value);
            if (account) {
              updated.accountCode = account.code;
              updated.accountName = account.name;
            }
          }
          return updated;
        }
        return line;
      })
    );
  };

  const totalDebit = lines.reduce((sum, line) => sum + (line.debit || 0), 0);
  const totalCredit = lines.reduce((sum, line) => sum + (line.credit || 0), 0);
  const difference = Math.abs(totalDebit - totalCredit);
  const isBalanced = difference < 0.01;

  const validateForm = (): boolean => {
    setError(null);

    if (!date) {
      setError('Date is required');
      return false;
    }

    if (!description.trim()) {
      setError('Description is required');
      return false;
    }

    if (lines.length < 2) {
      setError('At least two lines are required');
      return false;
    }

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line) continue;
      if (!line.accountId) {
        setError(`Line ${i + 1}: Account is required`);
        return false;
      }
      if (line.debit === 0 && line.credit === 0) {
        setError(`Line ${i + 1}: Either debit or credit amount is required`);
        return false;
      }
      if (line.debit > 0 && line.credit > 0) {
        setError(`Line ${i + 1}: Cannot have both debit and credit`);
        return false;
      }
    }

    if (!isBalanced) {
      setError(`Total debits (${formatCurrency(totalDebit, currency)}) must equal total credits (${formatCurrency(totalCredit, currency)})`);
      return false;
    }

    return true;
  };

  const handleSave = () => {
    if (!validateForm() || !user) return;

    setIsSubmitting(true);

    const lineData = lines.map((line) => ({
      accountId: line.accountId,
      description: line.description || undefined,
      debit: line.debit || 0,
      credit: line.credit || 0,
    }));

    if (isEditing) {
      updateMutation.mutate({
        id: id!,
        date: new Date(date || new Date()),
        description,
        referenceType: referenceType as any,
        currency,
        lines: lineData,
      });
    } else {
      createMutation.mutate({
        date: new Date(date || new Date()),
        description,
        referenceType: referenceType as any,
        currency,
        lines: lineData,
      });
    }
  };

  if (isEditing && isLoadingEntry) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isEditing && existingEntry?.status !== 'DRAFT') {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <p className="text-lg font-medium">{t('form.cannotEditEntry')}</p>
        <p className="text-muted-foreground mt-2">
          {t('form.cannotEditMessage', { status: existingEntry?.status.toLowerCase() })}
        </p>
        <Button variant="link" onClick={() => navigate(`/travel/accounting/journal-entries/${id}`)}>
          {t('form.viewEntryDetails')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/travel/accounting')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditing ? t('form.editJournalEntry') : t('form.newJournalEntry')}
            </h1>
            <p className="text-muted-foreground">
              {isEditing
                ? t('form.editing', { entryNumber: existingEntry?.entryNumber })
                : t('form.createSubtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-8" onClick={() => navigate('/travel/accounting')}>
            {t('form.cancel')}
          </Button>
          <Button className="h-8" onClick={handleSave} disabled={isSubmitting || !isBalanced}>
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isEditing ? t('form.saveChanges') : t('form.saveAsDraft')}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        </div>
      )}
      {accountsError && <p role="alert" className="text-sm text-red-700">{accountsError.message}</p>}

      {/* Entry Details */}
      <div className="border border-border bg-card p-6">
        <h3 className="text-lg font-semibold mb-4">{t('form.entryDetails')}</h3>
        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="date">{t('form.date')} *</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('form.referenceType')}</Label>
            <Combobox
              options={REFERENCE_TYPES.map((type) => ({
                value: type.value,
                label: type.label,
              }))}
              value={referenceType}
              onValueChange={setReferenceType}
              placeholder={t('form.selectType')}
              searchPlaceholder={t('form.search')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('form.currency')}</Label>
            <Combobox
              options={[
                { value: 'TRY', label: 'TRY' },
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
                { value: 'GBP', label: 'GBP' },
              ]}
              value={currency}
              onValueChange={setCurrency}
              placeholder={t('form.selectCurrency')}
              searchPlaceholder={t('form.search')}
            />
          </div>
          <div className="space-y-2 md:col-span-1">
            <Label htmlFor="description">{t('form.description')} *</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('form.descriptionPlaceholder')}
            />
          </div>
        </div>
      </div>

      {/* Journal Lines */}
      <div className="border border-border bg-card">
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t('form.journalLines')}</h3>
          <Button variant="outline" size="sm" className="h-8" onClick={addLine}>
            <Plus className="mr-2 h-4 w-4" />
            {t('form.addLine')}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium w-[300px]">{t('form.account')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium">{t('form.description')}</th>
                <th className="px-4 py-3 text-right text-sm font-medium w-[150px]">{t('form.debit')}</th>
                <th className="px-4 py-3 text-right text-sm font-medium w-[150px]">{t('form.credit')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium w-[60px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {lines.map((line) => (
                <tr key={line.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2">
                    <Combobox
                      options={allAccounts?.accounts.map((account) => ({
                        value: account.id,
                        label: `${account.code} - ${account.name}`,
                      })) || []}
                      value={line.accountId}
                      onValueChange={(value) => updateLine(line.id, 'accountId', value)}
                      placeholder={t('form.selectAccount')}
                      searchPlaceholder={t('form.searchAccounts')}
                      className="w-full"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      value={line.description}
                      onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                      placeholder={t('form.lineDescription')}
                      className="w-full"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.debit || ''}
                      onChange={(e) => updateLine(line.id, 'debit', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="text-right font-mono"
                      disabled={line.credit > 0}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.credit || ''}
                      onChange={(e) => updateLine(line.id, 'credit', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="text-right font-mono"
                      disabled={line.debit > 0}
                    />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLine(line.id)}
                      disabled={lines.length <= 2}
                      className="text-muted-foreground hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/30 font-medium">
              <tr>
                <td className="px-4 py-3" colSpan={2}>
                  <span className="text-sm">{t('form.totals')}</span>
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {formatCurrency(totalDebit, currency)}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {formatCurrency(totalCredit, currency)}
                </td>
                <td></td>
              </tr>
              {!isBalanced && (
                <tr className="text-red-600">
                  <td className="px-4 py-2" colSpan={2}>
                    <span className="text-sm flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {t('form.outOfBalance')}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono" colSpan={2}>
                    {t('form.difference')}: {formatCurrency(difference, currency)}
                  </td>
                  <td></td>
                </tr>
              )}
              {isBalanced && totalDebit > 0 && (
                <tr className="text-green-600">
                  <td className="px-4 py-2" colSpan={4}>
                    <span className="text-sm">{t('form.entryBalanced')}</span>
                  </td>
                  <td></td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      {/* Tips */}
      <div className="border border-blue-200 bg-blue-50 p-4 text-blue-700 text-sm">
        <p className="font-medium mb-1">{t('tips.title')}</p>
        <ul className="list-disc list-inside space-y-1">
          <li>{t('tips.tip1')}</li>
          <li>{t('tips.tip2')}</li>
          <li>{t('tips.tip3')}</li>
        </ul>
      </div>
    </div>
  );
}
